import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Stack, Typography, useTheme } from '@mui/material'

import { FieldRow } from 'components/Fields/FieldRow'
import {
  ListRow,
  PageContainer,
  PageHeader,
  SectionLabel,
  ToolbarSearchField,
  useTermSheet,
} from 'components/UI'
import { PAGE_GUTTER, PAGE_GUTTER_MOBILE, PROSE_MAX_WIDTH } from 'constants/layout'
import { CONSUMER_GLOSSARY } from 'docs/glossary'
import { TOP_QUESTIONS, searchHelp, type SearchResult } from 'help/articles'
import { usePushPage } from 'hooks/usePushPage'
import { font, space } from 'settings/tokens'

import { HELP_DEFAULT_BACK, helpArticlePath, type HelpNavState } from './navState'

/* ------------------------------------------------------------------ *
 * Pinned-block geometry
 *
 * Two surfaces pin here: the SEARCH BAND at the viewport top, and the
 * SECTION LABEL directly beneath it. The label's `top` is therefore not a
 * chosen number — it is the search band's rendered height, and every term
 * below is stated so the two cannot drift apart.
 *
 * ⚠️ `fieldMetrics.FIELD_HEIGHT` (40) is the WRONG token for this offset, and
 * it is the obvious one to reach for. 40 is the TEXT-FIELD band — `Input`,
 * `PhoneField`, `FieldRow`'s trigger. A toolbar search field is the other band:
 * `ToolbarSearchField` sets `height: '48px'` on the `@repo/ui` `Input` root, for
 * the reason `fieldMetrics`' own note on `toolbarControlFieldRowSx` records
 * ("anything abreast of a `ToolbarSearchField`" is 48, because a height
 * difference is only visible between neighbours). Using 40 would pin the label
 * 8px INTO the field. The 48 is not exported as a bare constant today — see the
 * note on `SEARCH_FIELD_HEIGHT`.
 * ------------------------------------------------------------------ */

/**
 * The rendered height of `ToolbarSearchField`.
 *
 * Read off that component's own `sx` (`height: '48px'`), which is the only
 * thing that sets it: `@repo/ui`'s `Input` wraps the control in a bare `Box`
 * with no margin, `FieldLabel` returns `null` with no `label`, there is no
 * `helperText`, and `ToolbarSearchField` explicitly zeroes the legacy
 * `marginTop`. So the field's box IS 48px, with nothing above or below it.
 *
 * Local because `ToolbarSearchField` does not export it. It should be hoisted
 * into `fieldMetrics.ts` beside `toolbarControlFieldRowSx` (whose `height: 48`
 * is derived from this same number) so the field and everything that pins under
 * it read one value — flagged rather than done here, since that file is outside
 * this change.
 */
const SEARCH_FIELD_HEIGHT = 48

/**
 * Air above the search field INSIDE the pinned band.
 *
 * 8, matching `SearchFilterSortBar`'s own `pt` — this app's other pinned search
 * toolbar — rather than a fresh guess. It does two jobs: pinned, it keeps the
 * field off the very top edge of the viewport; at rest, it widens
 * `PageHeader`'s 20px box-to-box gap to 28px, which is closer to the ~29px
 * reference that component's docblock derives (a section heading's gap to its
 * first row) than the 20px it replaces.
 */
const SEARCH_BAND_PT = 8

/**
 * Where the section label pins — the search band's full height, nothing else.
 *
 * The band has NO bottom padding, so this is `pt + field`. The air between the
 * pinned field and the pinned label below it is the LABEL band's `pt` instead
 * (see `SECTION_BAND_PT`), which is what keeps the gap identical whether the
 * label follows the search field or the previous section's last row.
 */
const SEARCH_BAND_HEIGHT = SEARCH_BAND_PT + SEARCH_FIELD_HEIGHT

/** Air above the label, inside its pinned band. */
const SECTION_BAND_PT = 12

/**
 * Label → first row. 8px, i.e. exactly `SectionLabel`'s own `mb: 1`, which the
 * `inline` prop switches off so this band owns the gap instead (a margin on a
 * sticky box is added to its `top`, so the label's own margins cannot stay —
 * see the note at the call site).
 */
const SECTION_BAND_PB = 8

/**
 * The section gap, in normal flow ABOVE the pinned band.
 *
 * `SectionLabel` normally opens a section with `mt: 4` (32px). That total is
 * preserved exactly — 20px of flow spacer plus the band's own 12px `pt` — but
 * it is SPLIT deliberately: only the 12px is inside the pinned box, so a pinned
 * label shows 12px of air above it rather than a 32px empty band eating a
 * fifth of a 375x812 screen.
 */
const SECTION_GAP_ABOVE = 32 - SECTION_BAND_PT

/**
 * Cancel the column's gutter so a pinned band reaches the screen edge.
 *
 * Not cosmetic: the rows below are `FieldRow fullBleed`, which pulls them 16px
 * out past the gutter, so a band stopping at the gutter would let row text
 * scroll past it down both sides. Same responsive pair `PageHeader` uses for
 * its own pinned block, and for the same reason recorded there — the gutter is
 * 16 at `xs` and 24 at `md`, so a flat `-16` leaves an 8px unbanded sliver at
 * desktop width.
 */
const bleedSx = {
  mx: { xs: `-${PAGE_GUTTER_MOBILE}px`, md: `-${PAGE_GUTTER}px` },
  px: { xs: `${PAGE_GUTTER_MOBILE}px`, md: `${PAGE_GUTTER}px` },
} as const

/**
 * The pinned search band.
 *
 * OPAQUE `background.paper` — the page's own fill — rather than the app's glass
 * (`utils/glass`). Glass is what `SearchFilterSortBar` and `PageHeader` use, and
 * it is the right call THERE because both flip to it only once stuck, which
 * needs a scroll/IntersectionObserver read and an inline-style escape hatch for
 * emotion's class caching. Nothing here changes on pin, so there is no stuck
 * state to detect and nothing that can paint the wrong one — and an opaque band
 * in the page's own colour is invisible at rest, which is what lets it carry no
 * chrome at all.
 *
 * `zIndex: 3` — the layer `PageHeader` takes for the same job, under MUI's
 * drawer level so the glossary term sheet still opens over it.
 */
const searchBandSx = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
  backgroundColor: 'background.paper',
  pt: `${SEARCH_BAND_PT}px`,
  ...bleedSx,
} as const

/** The pinned section label, one layer below the search band it sits under. */
const sectionBandSx = {
  position: 'sticky',
  top: `${SEARCH_BAND_HEIGHT}px`,
  zIndex: 2,
  backgroundColor: 'background.paper',
  pt: `${SECTION_BAND_PT}px`,
  pb: `${SECTION_BAND_PB}px`,
  ...bleedSx,
} as const

/**
 * One section of the browse view: a pinned label with its rows under it.
 *
 * ## The wrapper is load-bearing, not tidiness
 *
 * A sticky element is constrained to its CONTAINING BLOCK. With both labels as
 * direct children of the page column they would share one containing block and
 * both pin at the same `top` — so "Top questions" would stay pinned for the
 * whole page and "Glossary" would arrive stacked exactly on top of it. Giving
 * each section its own box means a label is released when its own section
 * scrolls out, and the next one takes the slot: the standard list-header
 * hand-off.
 *
 * ## The spacer, and why the gap is not a margin
 *
 * A sticky box's offsets constrain its MARGIN box, so `margin-top: 32px` on a
 * `top: 56px` sticky element pins it at 88 — leaving a 32px window of rows
 * scrolling in clear view between the search field and the label. That is why
 * `SectionLabel` is rendered `inline` (its own `mt`/`mb` off) and the gap lives
 * in a flow spacer that scrolls away normally. `inline` is documented for a
 * label sharing a row; the behaviour it provides — "drops both margins so the
 * container controls the spacing" — is exactly what is wanted here.
 */
const HelpSection = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <Box aria-hidden sx={{ height: `${SECTION_GAP_ABOVE}px` }} />
    <Box sx={sectionBandSx}>
      <SectionLabel inline>{label}</SectionLabel>
    </Box>
    <Stack>{children}</Stack>
  </Box>
)

/**
 * The consumer Help index: search, the common questions, and the glossary.
 *
 * ## No skeletons, and that is not an oversight
 *
 * Working-conventions §19 puts a footprint-matching skeleton on any surface that renders
 * async data. Nothing here is async: both corpora are committed artifacts imported as
 * modules, so they are present in the first render and there is no loading state to
 * occupy. A skeleton would be a frame that never shows.
 *
 * ## The search REPLACES the page rather than filtering it
 *
 * A non-empty query hides Top questions and the glossary entirely. Filtering the two
 * sections in place would keep two headings on screen with one row under each and leave
 * the reader working out which list their answer came from; a result list is one ranked
 * answer to one question, which is what a search is for. The type label on each row is
 * how the two corpora stay distinguishable once they are merged.
 */
export const HelpPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { openTerm } = useTermSheet()

  const back = (location.state as HelpNavState | null) ?? HELP_DEFAULT_BACK

  /*
   * `pushOnly` because `/help` genuinely cannot be entered cold: the app runs on a
   * `MemoryRouter` and `/help` is not in `STANDALONE_ENTRY_PATHS` (see `index.tsx`), so
   * there is no URL anyone can type, bookmark or scan — every path here comes from a
   * screen already on the stack. That is the statement about the route the flag requires,
   * not a preference.
   */
  const { pageSx, goBack } = usePushPage(back.backTo, { pushOnly: true })

  const [query, setQuery] = useState('')
  const results = useMemo(() => searchHelp(query), [query])
  const searching = query.trim().length > 0

  const openArticle = (slug: string) => navigate(helpArticlePath(slug))

  return (
    <Stack
      sx={{
        ...pageSx,
        background: theme.palette.background.paper,
        // Fill the viewport so the page background does not stop at the end of the
        // content and expose the backdrop beneath it. Every full page here does this.
        minHeight: '100vh',
        // NO `px` (PageContainer owns the gutter) and NO `pt` (PageHeader owns the top
        // gap). Both are noted on Profile and Notifications for the same reason.
        pb: { xs: 4, md: 8 },
      }}
    >
      <PageContainer>
        <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
          <PageHeader
            backLabel={back.backLabel}
            onBack={goBack}
            title='Help'
            // §40: the scope sentence sits under the title, not at the foot of the page.
            // §15: two sentences rather than one em-dash-joined clause.
            description='Answers about checking an item, ownership and transfers. No account needed to read any of it.'
          />

          {/*
            PINNED. The field is the one control that acts on what you are
            scrolling, so from the glossary's tail "search" otherwise means
            scrolling back to the top — the same argument `SearchFilterSortBar`
            records for the certificates list. It is also what makes the section
            label's pin coherent: the label pins DIRECTLY BENEATH a field that is
            still there, rather than under a gap the field has scrolled out of.
          */}
          <Box sx={searchBandSx}>
            <ToolbarSearchField
              onChange={setQuery}
              placeholder='Search help'
              ariaLabel='Search help questions and terms'
              // Client-side filter over 19 bundled records. There is no request to spare,
              // and a delay on a list this small only makes typing feel laggy.
              debounceMs={0}
            />
          </Box>

          {searching ? (
            <SearchResults
              query={query}
              results={results}
              onOpenArticle={openArticle}
              onOpenTerm={openTerm}
            />
          ) : (
            <>
              <HelpSection label='Top questions'>
                {TOP_QUESTIONS.map(article => (
                  <FieldRow
                    key={article.slug}
                    label={article.title}
                    // `placeholder=''` is load-bearing: FieldRow defaults it to "Add",
                    // which is right for an empty settings FIELD and nonsense on a
                    // navigation row. Same note as Profile's Notifications row.
                    placeholder=''
                    fullBleed
                    onClick={() => openArticle(article.slug)}
                  />
                ))}
              </HelpSection>

              {/*
                The glossary rows open a SHEET, not a page, and still take the chevron.
                That is this app's established language rather than a mismatch — Profile's
                General rows are `FieldRow`s that open bottom sheets too. The chevron says
                "there is more behind this", which is true either way.
              */}
              <HelpSection label='Glossary'>
                {CONSUMER_GLOSSARY.map(entry => (
                  <FieldRow
                    key={entry.id}
                    label={entry.term}
                    placeholder=''
                    fullBleed
                    onClick={() => openTerm(entry.id)}
                  />
                ))}
              </HelpSection>
            </>
          )}
        </Box>
      </PageContainer>
    </Stack>
  )
}

interface SearchResultsProps {
  query: string
  results: SearchResult[]
  onOpenArticle: (slug: string) => void
  onOpenTerm: (termId: string) => void
}

/**
 * The merged result list.
 *
 * `ListRow` rather than `FieldRow`, and the difference is the second line: a result needs
 * its summary to say WHY it matched, where a curated question ("What is a certificate?")
 * says it in the title. `ListRow` has no tap handling of its own, so each row is wrapped
 * in its own target — the pattern the Activity feed already uses, and the reason
 * `ListRow` takes an explicit `divider` (a wrapped row can never resolve `:last-of-type`
 * for itself).
 */
const SearchResults = ({ query, results, onOpenArticle, onOpenTerm }: SearchResultsProps) => {
  const theme = useTheme()

  if (results.length === 0) {
    return (
      <Stack sx={{ pt: 3 }} spacing={1}>
        <Typography sx={{ fontSize: font.body, color: theme.palette.text.primary }}>
          No results for “{query.trim()}”.
        </Typography>
        <Typography sx={{ fontSize: font.label, color: theme.palette.text.secondary }}>
          Try a shorter word, or clear the search to browse everything.
        </Typography>
      </Stack>
    )
  }

  return (
    <Box sx={{ pt: 2 }}>
      {results.map((result, index) => {
        const divider = index !== results.length - 1
        const isArticle = result.kind === 'article'
        const label = isArticle ? result.article.title : result.entry.term
        const sublabel = isArticle ? result.article.summary : result.entry.short
        const onClick = isArticle
          ? () => onOpenArticle(result.article.slug)
          : () => onOpenTerm(result.entry.id)

        return (
          <Box
            key={isArticle ? `a:${result.article.slug}` : `t:${result.entry.id}`}
            role='button'
            tabIndex={0}
            onClick={onClick}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick()
              }
            }}
            sx={{
              cursor: 'pointer',
              // Full-bleed press feedback, pulled out past the page gutter so the
              // highlight covers the whole tap target rather than just the text.
              mx: `-${space.sm}px`,
              px: `${space.sm}px`,
              '@media (hover: hover)': {
                '&:hover': { backgroundColor: theme.palette.action.hover },
              },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.text.primary}`,
                outlineOffset: '-2px',
              },
            }}
          >
            <ListRow
              label={label}
              sublabel={sublabel}
              divider={divider}
              meta={
                /*
                  The type label, and it is not decoration: once the two corpora are
                  merged, "Verified" the question and "Verified" the term are one word
                  apart, and the reader has to know which one they are about to open —
                  one navigates to a page, the other opens a sheet over this one.

                  "Question", not "Article". The section above this list is headed "Top
                  questions", so a badge reading ARTICLE gave the same corpus two names on
                  one screen — and the reader has no way to know they mean the same thing.
                  Question is also the truer word: every one of these is titled as a
                  question, and it is what the reader typed into the box. `HelpArticle`
                  stays the name in the code, where the audience is different.
                */
                <Typography
                  variant='body2'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isArticle ? 'Question' : 'Term'}
                </Typography>
              }
            />
          </Box>
        )
      })}
    </Box>
  )
}

export default HelpPage
